/**
 *  This module exports all modules which should be visible to the Backbone App
 */
import React from 'react';
import ReactDOM from 'react-dom';

import App from 'common/backbone-app';
import moment from 'common/moment';
import { initUnsecureSalesforceBot } from 'common/salesforce-bot/index';
import { SalesforceBotInitializer } from 'common/salesforce-bot/initializer';
import { SupportChatWidgetInitializer } from 'common/support-chat/initializer';
import { Initializer } from 'common/types/initializers';
import Wahanda from 'common/wahanda';
import { AppointmentDetailsInitializer } from 'components/calendar/CalendarEventEditor/AppointmentDetails/initializer';
import { AppointmentFeeInfoInitializer } from 'components/calendar/CalendarEventEditor/AppointmentFeeInfo/initializer';
import { AppointmentTooltipInitializer } from 'components/calendar/CalendarEventEditor/AppointmentTooltip/initializer';
import { CancellationInitializer } from 'components/calendar/CalendarEventEditor/Cancellation/initializer';
import { EditTimeBlockInitializer } from 'components/calendar/CalendarEventEditor/EditTimeBlock/initializer';
import { NewTimeSlotInitializer } from 'components/calendar/CalendarEventEditor/NewTimeSlot/initializer';
import { NoShowV2ConfirmationModalInitializer } from 'components/calendar/CalendarEventEditor/NoShowV2/NoShowV2ConfirmationModal/initializer';
import { RequirePrepaymentModalInitializer } from 'components/calendar/CalendarEventEditor/NoShowV2/RequirePrepaymentModal/initializer';
import { CustomerFormLinksInitializer } from 'components/calendar/CustomerFormLinks/initializer';
import { RescheduleFeedbackModalInitializer } from 'components/calendar/Reschedule/RescheduleFeedbackModal/initializer';
import { WaitingListInitializer } from 'components/calendar/WaitingList/initializer';
import { ClientAppointmentsModalInitializer } from 'components/clients/ClientAppointmentsModal/initializer';
import { ClientEditDetailsDialogInitializer } from 'components/clients/ClientEditDetailsDialog/initializer';
import { ClientListEmptyStateInitializer } from 'components/clients/ClientListEmptyState/initializer';
import { ClientListFooterInitializer } from 'components/clients/ClientListFooter/initializer';
import { ClientListImportModalInitializer } from 'components/clients/ClientListImportModal/initializer';
import { BookableOnTreatwellBannerInitializer } from 'components/clients/BookableOnTreatwellBanner/initializer';
import { HeaderSearchClientResultsInitializer } from 'components/header/HeaderSearchClientResults/initializer';
import { ClientOverviewInitializer } from 'components/clients/ClientOverview/initializer';
import { ExportReasonDialogInitializer } from 'components/clients/ExportReasonDialog/initializer';
import { ImageEditDialogInitializer } from 'components/common/dialog/ImageEditDialog/initializer';
import { PhotoThumbnailListInitializer } from 'components/common/PhotoThumbnailList/initializer';
import { PhotoUploadInitializer } from 'components/common/PhotoUpload/initializer';
/*
 *  Initializers
 */
import { SidebarInfoInitializer } from 'components/common/Sidebar/SidebarInfo/initializer';
import { KycBannerInitializer } from 'components/KycBanner/initializer';
import { PhotosGuideSidebarInitializer } from 'components/settings/PhotosGuideSidebar/initializer';
import { OnboardingInitializer } from 'components/shared/Onboarding/initializer';
import { OnboardingWidgetInitializer } from 'components/shared/OnboardingWidget/initializer';
import { SetupProgressInitializer } from 'components/shared/SetupProgress/initializer';
import { Tracking } from 'src/tracking';
import FloatingEventHandler from 'utilities/FloatingEventHandler';
import { initSupportChat } from 'utilities/support-chat';

import { AppointmentRepeatRowInitializer } from './components/calendar/CalendarEventEditor/AppointmentRepeatRow/initializer';
import { NoShowOverPhoneInitializer } from './components/calendar/CalendarEventEditor/NoShow/NoShowOverPhone/initializer';
import { PrepayRequireDialogInitializer } from './components/calendar/CalendarEventEditor/NoShow/PrepayRequireDialog/initializer';
import { ResourceCheckWarningInitializer } from './components/calendar/CalendarEventEditor/ResourceCheckWarning/initializer';
import { EmployeeWorkingHoursToggleInitializer } from './components/calendar/EmployeeWorkingHoursToggle/initializer';
import { IdentityVerificationModalInitializer } from './components/calendar/IdentityVerificationModal/initializer';
import { CheckoutButtonInitializer } from './components/calendar/SidebarActions/CheckoutButton/initializer';
import { ClientAddOrEditInitializer } from './components/clients/ClientAddOrEdit/initializer';
import { ClientsImport } from './components/clients/ClientsImport/initializer';
import { DialogCloseWithChangesInitializer } from './components/common/dialog/DialogCloseWithChanges/initializer';
import { DialogConfirmationInitializer } from './components/common/dialog/DialogConfirmation/initializer';
import { LayoutHeaderInitializer } from './components/common/Layout/LayoutHeader/initializer';
import { NoticeInitializer } from './components/common/Notice/initializer';
import { SelectInitializer } from './components/common/Select/initializer';
import { CustomerFormDialogInitializer } from './components/customers/CustomerFormDialog/initializer';
import { LoginPageInitializer } from './components/login/initializer';
import { AddServicesModalInitializer } from './components/menu/AddServicesModal/initializer';
import { MenuGroupModalInitializer } from './components/menu/MenuGroupModal/initializer';
import { OfferTypeChoiceDialogInitializer } from './components/menu/OfferTypeChoiceDialog/initializer';
import { PatchTestRequired } from './components/menu/PatchTestRequired/initializer';
import { SelectServiceDialogInitializer } from './components/menu/SelectServiceDialog/initializer';
import { ReceiptCopyDialogInitializer } from './components/pos/ReceiptCopyDialog/initializer';
import { ReportsInitializer } from './components/reports/Reports/initializer';
import { ReportsMainView } from './components/reports/Reports/MainView';
import { RootTransactionDialogInitializer } from './components/reports/transactions/RootTransactionDialog/initializer';
import { paymentsInitializer } from './components/settings/PaymentMessage/initializer';
import { DurationSelectorInitializer } from './components/shared/DurationSelector/initializer';
import { PaymentProtectionBadgeInitializer } from './components/shared/PaymentProtectionBadge/initializer';
import { ShiftEmployeeModalInitializer } from './components/shifts/ShiftEmployeeModal/initializer';
import { ShiftVenueModalInitializer } from './components/shifts/ShiftVenueModal/initializer';
import { TeamLayoutInitializer } from './components/team/initializer';
import { AccountLanguageSelect } from './initializers/AccountLanguageSelect';
import initClientReports from './initializers/ClientReports';
import clientsBulkDelete from './initializers/ClientsBulkDelete';
import initCalendarDropdowns from './initializers/calendar-dropdowns';
import { CheckoutSummaryDialogInitializer } from './initializers/checkout-summary-dialog';
import { QuoteSummaryDialogInitializer } from './initializers/quote-summary-dialog';
import renderDirectDebitBankDetailsChangeConfirmation from './initializers/DirectDebitBankDetailsChangeConfirmation';
import showDiscountRuleDialog from './initializers/DiscountRuleDialog';
import renderDiscountRuleMenuRow from './initializers/DiscountRuleMenuRow';
import {
  destroy as destroyDiscountsTab,
  render as renderDiscountsTab,
} from './initializers/DiscountsTab';
import { DashboardInitializer } from './initializers/dashboard';
import { EmployeeDialogInitializer } from './initializers/EmployeeDialog';
import ErrorDialog from './initializers/ErrorDialog';
import externalSalonIntegration from './initializers/ExternalSalonIntegration';
import impressum from './initializers/Impressum';
import { destroy as destroyMenu, render as renderMenu } from './initializers/Menu';
import { MarketingInitializer } from './initializers/marketing';
import notificationList from './initializers/NotificationList';
import notificationListHeader from './initializers/NotificationListHeader';
import {
  destroy as destroyMenuPackagePricing,
  render as renderMenuPackagePricing,
} from './initializers/PackagePricing';
import productDialog from './initializers/ProductDialog';
import {
  destroy as destroyProductsTab,
  render as renderProductsTab,
} from './initializers/ProductsTab';
import posCheckoutCheck from './initializers/pos-checkout-check';
import { canNavigateAway, initPOSSettings, unmountPOSSettings } from './initializers/posSettings';
import ReactGlobal from './initializers/ReactGlobal';
import RebookingChangesDialog from './initializers/RebookingChanges';
import { injectResourcesList, venueChangeInitiated } from './initializers/resources';
import initSalesReports from './initializers/SalesReports';
import SettingsOnlineWidget from './initializers/SettingsOnlineWidget';
import changeStateExternally from './initializers/StateChange';
import './mfa-aspect';
import { HubSpot } from './tracking/lib/hubspot';

/*
 *  Export into the Backbone namespace
 */
if (!App.ES6.React) {
  App.ES6.React = React;
  App.ES6.ReactDOM = ReactDOM;
  App.ES6.moment = moment;
}

window.React = React;
window.ReactDOM = ReactDOM;
window.moment = moment;

Wahanda.Tracking = Tracking;
Wahanda.FloatingEventHandler = FloatingEventHandler;
Wahanda.POSCheckoutCheck = posCheckoutCheck;
Wahanda.initUnsecureSalesforceBot = initUnsecureSalesforceBot;
Wahanda.initSupportChat = initSupportChat;
Wahanda.HubSpot = HubSpot;

App.ES6.Initializers.State = { change: changeStateExternally };
App.ES6.Initializers.ReactGlobal = ReactGlobal;

// Common
App.ES6.Initializers.Notice = NoticeInitializer;
App.ES6.Initializers.ConfirmationDialog = DialogConfirmationInitializer;
App.ES6.Initializers.DialogCloseWithChanges = DialogCloseWithChangesInitializer;
App.ES6.Initializers.ErrorListDialog = ErrorDialog;
App.ES6.Initializers.LayoutHeader = LayoutHeaderInitializer;
App.ES6.Initializers.Select = SelectInitializer;
App.ES6.Initializers.CheckoutButton = CheckoutButtonInitializer;
App.ES6.Initializers.SidebarInfo = SidebarInfoInitializer;
// The COVID banner was deleted in CCM-2120, but Backbone's header-view.js still mounts
// and destroys it on every header render. This no-op keeps those calls harmless until
// the Backbone hooks are removed; delete it together with them.
App.ES6.Initializers.CovidBanner = (): Initializer => ({
  destroy: () => undefined,
  render: () => undefined,
});
App.ES6.Initializers.LoginPage = LoginPageInitializer;
App.ES6.Initializers.IdentityVerificationModal = IdentityVerificationModalInitializer;
App.ES6.Initializers.ImageEditDialogInitializer = ImageEditDialogInitializer;
App.ES6.Initializers.SalesforceBotInitializer = SalesforceBotInitializer;
App.ES6.Initializers.SupportChatWidgetInitializer = SupportChatWidgetInitializer;

// Shared
App.ES6.Initializers.AccountLanguageSelect = AccountLanguageSelect;
App.ES6.Initializers.CustomerFormDialog = CustomerFormDialogInitializer;
App.ES6.Initializers.KycBanner = KycBannerInitializer;

// Calendar
App.ES6.Initializers.CalendarDropdowns = { initialize: initCalendarDropdowns };
App.ES6.Initializers.DurationSelector = DurationSelectorInitializer;
App.ES6.Initializers.AppointmentRepeatRow = AppointmentRepeatRowInitializer;
App.ES6.Initializers.EmployeeWorkingHoursToggle = EmployeeWorkingHoursToggleInitializer;
App.ES6.Initializers.NoShowOverPhoneDialog = NoShowOverPhoneInitializer;
App.ES6.Initializers.PrepayRequireDialog = PrepayRequireDialogInitializer;
App.ES6.Initializers.RebookingChangesDialog = RebookingChangesDialog;
App.ES6.Initializers.CustomerFormLinks = CustomerFormLinksInitializer;
App.ES6.Initializers.ResourceCheckWarning = ResourceCheckWarningInitializer;
App.ES6.Initializers.Cancellation = CancellationInitializer;
App.ES6.Initializers.AppointmentDetails = AppointmentDetailsInitializer;
App.ES6.Initializers.AppointmentTooltip = AppointmentTooltipInitializer;
App.ES6.Initializers.AppointmentFeeInfo = AppointmentFeeInfoInitializer;
App.ES6.Initializers.NewTimeSlot = NewTimeSlotInitializer;
App.ES6.Initializers.NoShowV2ConfirmationModal = NoShowV2ConfirmationModalInitializer;
App.ES6.Initializers.RequirePrepaymentModal = RequirePrepaymentModalInitializer;
App.ES6.Initializers.EditTimeBlock = EditTimeBlockInitializer;
App.ES6.Initializers.WaitingList = WaitingListInitializer;
App.ES6.Initializers.RescheduleFeedbackModal = RescheduleFeedbackModalInitializer;

// Clients
App.ES6.Initializers.ClientsBulkDelete = clientsBulkDelete;
App.ES6.Initializers.ClientsImport = ClientsImport;
App.ES6.Initializers.ClientListImportModal = ClientListImportModalInitializer;
App.ES6.Initializers.ClientAddOrEdit = ClientAddOrEditInitializer;
App.ES6.Initializers.HeaderSearchClientResults = HeaderSearchClientResultsInitializer;
App.ES6.Initializers.ClientListEmptyState = ClientListEmptyStateInitializer;
App.ES6.Initializers.ClientListFooter = ClientListFooterInitializer;
App.ES6.Initializers.BookableOnTreatwellBanner = BookableOnTreatwellBannerInitializer;
App.ES6.Initializers.ClientOverview = ClientOverviewInitializer;
App.ES6.Initializers.ClientEditDetailsDialog = ClientEditDetailsDialogInitializer;
App.ES6.Initializers.ClientAppointmentsModal = ClientAppointmentsModalInitializer;
App.ES6.Initializers.ExportReasonDialog = ExportReasonDialogInitializer;

// Dashboard
App.ES6.Initializers.Dashboard = DashboardInitializer;

// Marketing
App.ES6.Initializers.Marketing = MarketingInitializer;
App.ES6.Initializers.Impressum = impressum;

// Menu
App.ES6.Initializers.Menu = { render: renderMenu, destroy: destroyMenu };
App.ES6.Initializers.DiscountsTab = {
  render: renderDiscountsTab,
  destroy: destroyDiscountsTab,
};
App.ES6.Initializers.MenuGroupModal = MenuGroupModalInitializer;
App.ES6.Initializers.AddServicesModal = AddServicesModalInitializer;
App.ES6.Initializers.menuOfferPackagePricing = {
  render: renderMenuPackagePricing,
  destroy: destroyMenuPackagePricing,
};
App.ES6.Initializers.OfferTypeChoiceDialog = OfferTypeChoiceDialogInitializer;
App.ES6.Initializers.PatchTestRequired = PatchTestRequired;
App.ES6.Initializers.renderDiscountRuleMenuRow = renderDiscountRuleMenuRow;
App.ES6.Initializers.SelectServiceDialog = SelectServiceDialogInitializer;
App.ES6.Initializers.showGroupDiscountEditor = showDiscountRuleDialog;

// Notification
App.ES6.Initializers.NotificationsList = notificationList;
App.ES6.Initializers.NotificationsListHeader = notificationListHeader;

// POS
App.ES6.Initializers.POSSettings = {
  inject: initPOSSettings,
  canNavigateAway,
  unmountPOSSettings,
};
App.ES6.Initializers.PaymentMessage = paymentsInitializer;
App.ES6.Initializers.ReceiptCopyDialog = ReceiptCopyDialogInitializer;

// Products
App.ES6.Initializers.ProductsTab = {
  render: renderProductsTab,
  destroy: destroyProductsTab,
};
App.ES6.Initializers.ProductDialog = productDialog;

// Reports
const previousReportsViews = App.Views.Reports;
const legacyReportsViews =
  previousReportsViews == null ? {} : ({ ...previousReportsViews } as Record<string, unknown>);
App.Views.Reports = Object.assign({}, legacyReportsViews, { Main: ReportsMainView });
App.ES6.Initializers.Reports = ReportsInitializer;
App.ES6.Initializers.CheckoutSummaryDialog = CheckoutSummaryDialogInitializer;
App.ES6.Initializers.QuoteSummaryDialog = QuoteSummaryDialogInitializer;
App.ES6.Initializers.clientReports = initClientReports;
App.ES6.Initializers.RootTransactionDialogInitializer = RootTransactionDialogInitializer;
App.ES6.Initializers.salesReports = initSalesReports;

// Team
App.ES6.Initializers.Team = TeamLayoutInitializer;
App.ES6.Initializers.EmployeeDialog = EmployeeDialogInitializer;
App.ES6.Initializers.ShiftVenueModal = ShiftVenueModalInitializer;
App.ES6.Initializers.ShiftEmployeeModal = ShiftEmployeeModalInitializer;

// Settings
App.ES6.Initializers.ExternalSalonIntegration = externalSalonIntegration;
App.ES6.Initializers.renderDirectDebitBankDetailsChangeConfirmation = renderDirectDebitBankDetailsChangeConfirmation;
App.ES6.Initializers.Resources = {
  inject: injectResourcesList,
  venueChangeInitiated,
};
App.ES6.Initializers.SettingsOnlineWidget = SettingsOnlineWidget;
App.ES6.Initializers.OnboardingWidget = OnboardingWidgetInitializer;
App.ES6.Initializers.Onboarding = OnboardingInitializer;
App.ES6.Initializers.SetupProgress = SetupProgressInitializer;
App.ES6.Initializers.PaymentProtectionBadge = PaymentProtectionBadgeInitializer;
App.ES6.Initializers.PhotosGuideSidebar = PhotosGuideSidebarInitializer;
App.ES6.Initializers.PhotoUpload = PhotoUploadInitializer;
App.ES6.Initializers.PhotoThumbnailList = PhotoThumbnailListInitializer;
